<template>
    <Container judul="Beranda">
        <template #header>
            <HeaderFe :class="{ 'relative z-50': isDisplayMode }" />
        </template>
        <template v-slot:default>
            <div class="flex flex-col min-h-screen">
                <main class="flex flex-col min-h-screen pt-16 pb-10">

                    <div class="container px-4 py-8 mx-auto">
                        <!-- Display Mode Toggle -->
                        <div class="flex items-center justify-between mb-4" :class="{
                            'fixed top-4 left-4 right-4 z-50 bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg':
                                isDisplayMode,
                        }">
                            <!-- Clock and Date (visible in display mode) -->
                            <div v-if="isDisplayMode" class="text-gray-700">
                                <div class="text-3xl font-bold">{{ currentTime }}</div>
                                <div class="text-lg">{{ currentDate }}</div>
                                <!-- Auto-refresh status in display mode -->
                                <div v-if="autoRefreshEnabled" class="flex items-center mt-2 text-sm text-green-600">
                                    <i class="mr-1 fas fa-sync-alt"
                                        :class="{ 'animate-spin': refreshCountdown <= 3 }"></i>
                                    Refresh in {{ refreshCountdown }}s
                                    <span v-if="lastRefreshTime" class="ml-2 text-gray-500">
                                        (Last: {{ formatLastRefreshTime }})
                                    </span>
                                </div>
                                <!-- Refresh success indicator -->
                                <div v-if="showRefreshSuccess"
                                    class="flex items-center mt-2 text-sm text-green-600 animate-pulse">
                                    <i class="mr-1 fas fa-check-circle"></i>
                                    Data refreshed successfully
                                </div>
                            </div>

                            <!-- Simplified header in normal mode -->
                            <div v-else class="flex items-center space-x-2">
                                <h1 class="text-2xl font-bold text-gray-800">Dashboard FEB Info</h1>
                            </div>

                            <button @click="toggleDisplayMode"
                                class="px-4 py-2 text-white transition-colors bg-blue-600 rounded-lg shadow-lg hover:bg-blue-700"
                                :class="{ 'bg-red-600 hover:bg-red-700': isDisplayMode }">
                                <i class="mr-2 fas" :class="isDisplayMode ? 'fa-compress' : 'fa-expand'"></i>
                                {{ isDisplayMode ? "Exit Display" : "Display Mode" }}
                            </button>
                        </div>

                        <!-- Auto-refresh Floating Panel (visible when not in display mode) -->
                        <div v-if="!isDisplayMode"
                            class="fixed z-50 transition-all duration-300 bg-white border border-gray-200 rounded-lg shadow-lg auto-refresh-panel top-4 right-4"
                            :class="showAutoRefreshPanel ? 'translate-x-0' : 'translate-x-full'" style="width: 280px;">

                            <!-- Panel Header -->
                            <div
                                class="flex items-center justify-between p-3 border-b border-gray-200 rounded-t-lg bg-gray-50">
                                <div class="flex items-center space-x-2">
                                    <i class="text-blue-500 fas fa-sync-alt"
                                        :class="{ 'animate-spin': autoRefreshEnabled && refreshCountdown <= 3 }"></i>
                                    <h3 class="font-semibold text-gray-800">Auto Refresh</h3>
                                </div>
                                <button @click="showAutoRefreshPanel = false" class="text-gray-500 hover:text-gray-700">
                                    <i class="fas fa-times"></i>
                                </button>
                            </div>

                            <!-- Panel Content -->
                            <div class="p-4 space-y-4">
                                <!-- Refresh interval selector -->
                                <div>
                                    <label class="block mb-2 text-sm font-medium text-gray-700">Refresh Interval</label>
                                    <select v-model="autoRefreshInterval"
                                        @change="autoRefreshEnabled && setRefreshInterval(autoRefreshInterval)"
                                        class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                        <option value="10">Every 10 seconds</option>
                                        <option value="30">Every 30 seconds</option>
                                        <option value="60">Every 1 minute</option>
                                        <option value="300">Every 5 minutes</option>
                                        <option value="600">Every 10 minutes</option>
                                    </select>
                                </div>

                                <!-- Control buttons -->
                                <div class="flex space-x-2">
                                    <button @click="autoRefreshEnabled ? stopAutoRefresh() : startAutoRefresh()"
                                        class="flex-1 px-3 py-2 text-sm font-medium text-white transition-colors rounded-lg shadow-sm"
                                        :class="autoRefreshEnabled ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'">
                                        <i class="mr-2 fas" :class="autoRefreshEnabled ? 'fa-stop' : 'fa-play'"></i>
                                        {{ autoRefreshEnabled ? 'Stop' : 'Start' }}
                                    </button>

                                    <button @click="refreshPage"
                                        class="px-3 py-2 text-sm font-medium text-white transition-colors bg-blue-500 rounded-lg shadow-sm hover:bg-blue-600">
                                        <i class="fas fa-sync-alt"></i>
                                    </button>
                                </div>

                                <!-- Status display -->
                                <div v-if="autoRefreshEnabled" class="p-3 rounded-lg bg-gray-50">
                                    <div class="flex items-center justify-between mb-2">
                                        <span class="text-sm font-medium text-gray-700">Next refresh in:</span>
                                        <span class="text-lg font-bold"
                                            :class="refreshCountdown <= 5 ? 'text-red-600 countdown-urgent' : 'text-green-600'">
                                            {{ refreshCountdown }}s
                                        </span>
                                    </div>
                                    <div v-if="lastRefreshTime" class="text-xs text-gray-500">
                                        Last refreshed: {{ formatLastRefreshTime }}
                                    </div>
                                </div>

                                <!-- Success indicator -->
                                <div v-if="showRefreshSuccess"
                                    class="flex items-center p-2 text-sm text-green-700 bg-green-100 rounded-lg animate-pulse">
                                    <i class="mr-2 fas fa-check-circle"></i>
                                    Data refreshed successfully
                                </div>
                            </div>
                        </div>

                        <!-- Toggle button for auto-refresh panel -->
                        <button v-if="!isDisplayMode" @click="showAutoRefreshPanel = !showAutoRefreshPanel"
                            class="fixed z-40 p-3 text-white bg-blue-500 rounded-full shadow-lg floating-refresh-btn top-4 right-4 hover:bg-blue-600"
                            :class="showAutoRefreshPanel ? 'opacity-0 pointer-events-none' : 'opacity-100'"
                            title="Auto Refresh Settings">
                            <i class="fas fa-sync-alt"
                                :class="{ 'animate-spin': autoRefreshEnabled && refreshCountdown <= 3 }"></i>
                        </button>

                        <div class="grid grid-cols-1 gap-8 mb-8 transition-all duration-500 lg:grid-cols-3" :class="{
                            'fixed top-24 left-0 right-0 bottom-0 z-40 bg-gradient-to-br from-gray-50 to-gray-200 overflow-y-auto overflow-x-hidden display-mode-container': isDisplayMode,
                            'p-8 pt-12 pb-16': isDisplayMode && displayOptimization !== 'tv-720p',
                            'p-6 pt-8 pb-12': isDisplayMode && displayOptimization === 'tv-720p',
                            'xl:grid-cols-4': isDisplayMode && displayOptimization !== 'tv-720p',
                            'lg:grid-cols-2': isDisplayMode && displayOptimization === 'tv-720p',
                            'gap-8': isDisplayMode && displayOptimization !== 'tv-720p',
                            'gap-6': isDisplayMode && displayOptimization === 'tv-720p',
                        }" style="scroll-behavior: smooth;">
                            <div v-if="kehadiran_hari_ini.length > 0" :class="[
                                displayOptimization === 'tv-720p' ? 'lg:col-span-1' : 'lg:col-span-2',
                                isDisplayMode ? 'mb-6' : 'mb-0'
                            ]">
                                <div class="bg-white rounded-lg shadow-lg" :class="[
                                    { 'bg-gradient-to-br from-blue-50 to-blue-100': isDisplayMode },
                                    displayOptimization === 'tv-720p' ? 'p-4' : 'p-6'
                                ]">
                                    <h3 class="flex items-center mb-4 font-bold text-blue-600" :class="{
                                        'text-4xl mb-6': isDisplayMode && displayOptimization !== 'tv-720p',
                                        'text-3xl mb-4': isDisplayMode && displayOptimization === 'tv-720p',
                                        'text-2xl': !isDisplayMode
                                    }">
                                        <i class="mr-3 fas fa-user-check" :class="{
                                            'text-5xl': isDisplayMode && displayOptimization !== 'tv-720p',
                                            'text-4xl': isDisplayMode && displayOptimization === 'tv-720p'
                                        }"></i>
                                        Kehadiran Hari Ini
                                    </h3>
                                    <div class="relative">
                                        <!-- Carousel Container -->
                                        <div class="overflow-hidden rounded-lg">
                                            <div class="flex transition-transform duration-300 ease-in-out" :style="{
                                                transform: `translateX(-${currentSlide * 100}%)`,
                                            }">
                                                <!-- Slides -->
                                                <div v-for="(slide, index) in kehadiranSlides" :key="index"
                                                    class="flex-shrink-0 w-full">
                                                    <div class="grid grid-cols-1 gap-4 p-4 rounded-lg bg-gray-50 md:grid-cols-2"
                                                        :class="isTVDisplay ? 'lg:grid-cols-1 xl:grid-cols-2' : 'lg:grid-cols-2'">
                                                        <div v-for="kehadiran in slide" :key="kehadiran.id"
                                                            class="p-4 transition-all duration-300 bg-white border border-blue-200 rounded-lg hover:shadow-lg"
                                                            :class="{
                                                                'p-6 border-2': isDisplayMode,
                                                                'p-3': isTVDisplay,
                                                                'text-sm': isTVDisplay
                                                            }">
                                                            <div class="flex items-center mb-4">
                                                                <!-- Photo Display -->
                                                                <div class="flex-shrink-0 mr-4">
                                                                    <div class="relative overflow-hidden bg-gray-200 border-2 border-blue-300 rounded-full cursor-pointer kehadiran-photo"
                                                                        :class="isDisplayMode ? (isTVDisplay ? 'w-16 h-16' : 'w-20 h-20') : (isTVDisplay ? 'w-12 h-12' : 'w-16 h-16')"
                                                                        :title="`Foto ${kehadiran.nama_pimpinan}`">
                                                                        <!-- Loading state -->
                                                                        <div v-if="kehadiran.foto && !imageLoaded[kehadiran.id] && !imageErrors[kehadiran.id]"
                                                                            class="flex items-center justify-center w-full h-full text-white bg-gradient-to-br from-gray-400 to-gray-500">
                                                                            <i class="fas fa-spinner fa-spin"
                                                                                :class="isDisplayMode ? 'text-xl' : 'text-lg'"></i>
                                                                        </div>

                                                                        <!-- Photo -->
                                                                        <img v-if="kehadiran.foto && !imageErrors[kehadiran.id]"
                                                                            :src="getPhotoUrl(kehadiran.foto)"
                                                                            :alt="kehadiran.nama_pimpinan"
                                                                            class="object-cover w-full h-full transition-all duration-300"
                                                                            :class="{ 'opacity-0': !imageLoaded[kehadiran.id] }"
                                                                            @error="handleImageError(kehadiran.id)"
                                                                            @load="handleImageLoad(kehadiran.id)">

                                                                        <!-- Fallback/Default Avatar -->
                                                                        <div v-if="!kehadiran.foto || imageErrors[kehadiran.id]"
                                                                            class="flex flex-col items-center justify-center w-full h-full text-white bg-gradient-to-br from-blue-500 to-blue-600">
                                                                            <i class="mb-1 fas fa-user"
                                                                                :class="isDisplayMode ? 'text-2xl' : 'text-lg'"></i>
                                                                            <span class="text-xs opacity-75"
                                                                                v-if="isDisplayMode">No Photo</span>
                                                                        </div>
                                                                        <!-- Online status indicator -->
                                                                        <div class="absolute bottom-0 right-0 border-2 border-white rounded-full status-indicator"
                                                                            :class="[
                                                                                isDisplayMode ? 'w-5 h-5' : 'w-4 h-4',
                                                                                getStatusClass(kehadiran.status).includes('green') ? 'bg-green-500' :
                                                                                    getStatusClass(kehadiran.status).includes('red') ? 'bg-red-500' :
                                                                                        getStatusClass(kehadiran.status).includes('yellow') ? 'bg-yellow-500' :
                                                                                            getStatusClass(kehadiran.status).includes('blue') ? 'bg-blue-500' :
                                                                                                getStatusClass(kehadiran.status).includes('purple') ? 'bg-purple-500' : 'bg-gray-500'
                                                                            ]"></div>
                                                                    </div>
                                                                </div>
                                                                <div class="flex-1 min-w-0">
                                                                    <h4 class="font-semibold text-gray-800 truncate"
                                                                        :class="isDisplayMode ? 'text-xl mb-1' : 'text-base'">
                                                                        {{ kehadiran.nama_pimpinan }}
                                                                    </h4>
                                                                    <p class="text-gray-600 truncate"
                                                                        :class="isDisplayMode ? 'text-base mb-2' : 'text-sm mb-1'">
                                                                        {{ kehadiran.jabatan }}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div class="space-y-3">
                                                                <!-- Date Information -->
                                                                <div class="flex items-center"
                                                                    :class="isDisplayMode ? 'text-base' : 'text-sm'">
                                                                    <i class="mr-3 text-blue-500 fas fa-calendar"
                                                                        :class="isDisplayMode ? 'text-lg' : 'w-4'"></i>
                                                                    <span class="font-medium text-gray-700">{{
                                                                        formatDate(kehadiran.tanggal)
                                                                        }}</span>
                                                                </div>

                                                                <!-- Status - Made Larger and More Prominent -->
                                                                <div class="flex items-center justify-center">
                                                                    <span :class="[
                                                                        getStatusClass(kehadiran.status),
                                                                        'font-bold rounded-lg shadow-sm text-center flex items-center justify-center status-badge cursor-pointer',
                                                                        isDisplayMode ? 'px-6 py-3 text-lg min-w-[120px]' : 'px-4 py-2 text-sm min-w-[100px]'
                                                                    ]" :title="`Status: ${getStatusLabel(kehadiran.status)} - ${formatDate(kehadiran.tanggal)}`">
                                                                        <i class="mr-2 fas" :class="[
                                                                            kehadiran.status === 'hadir' ? 'fa-check-circle' :
                                                                                kehadiran.status === 'tidak_hadir' ? 'fa-times-circle' :
                                                                                    kehadiran.status === 'terlambat' ? 'fa-clock' :
                                                                                        kehadiran.status === 'sakit' ? 'fa-thermometer-half' :
                                                                                            kehadiran.status === 'izin' ? 'fa-calendar-check' : 'fa-question-circle',
                                                                            isDisplayMode ? 'text-base' : 'text-xs'
                                                                        ]"></i>
                                                                        {{ getStatusLabel(kehadiran.status) }}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Navigation Buttons -->
                                        <button v-if="kehadiranSlides.length > 1" @click="prevSlide"
                                            class="absolute left-0 p-2 text-white transform -translate-y-1/2 bg-blue-600 rounded-full shadow-lg top-1/2 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                                            <i class="fas fa-chevron-left"></i>
                                        </button>
                                        <button v-if="kehadiranSlides.length > 1" @click="nextSlide"
                                            class="absolute right-0 p-2 text-white transform -translate-y-1/2 bg-blue-600 rounded-full shadow-lg top-1/2 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                                            <i class="fas fa-chevron-right"></i>
                                        </button>

                                        <!-- Dots Indicator -->
                                        <div v-if="kehadiranSlides.length > 1"
                                            class="flex justify-center mt-4 space-x-2">
                                            <button v-for="(slide, index) in kehadiranSlides" :key="index"
                                                @click="currentSlide = index" :class="[
                                                    'w-3 h-3 rounded-full transition-all duration-200',
                                                    currentSlide === index
                                                        ? 'bg-blue-600'
                                                        : 'bg-gray-300 hover:bg-gray-400',
                                                ]"></button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Statistics Cards - Right Side (1/3 width) -->
                            <div class="space-y-4 lg:col-span-1" :class="[
                                { 'space-y-6': isDisplayMode },
                                isDisplayMode ? 'mb-6' : 'mb-0'
                            ]">
                                <div class="p-6 text-white transition-all duration-300 transform bg-blue-500 rounded-lg shadow-lg"
                                    :class="{
                                        'p-8 scale-105 shadow-2xl': isDisplayMode,
                                        'p-4': isTVDisplay
                                    }">
                                    <div class="flex items-center">
                                        <i class="mr-4 fas fa-user-check"
                                            :class="isDisplayMode ? (isTVDisplay ? 'text-4xl' : 'text-5xl') : (isTVDisplay ? 'text-2xl' : 'text-3xl')"></i>
                                        <div>
                                            <h3 class="font-semibold"
                                                :class="isDisplayMode ? (isTVDisplay ? 'text-xl' : 'text-2xl') : (isTVDisplay ? 'text-base' : 'text-lg')">
                                                Kehadiran Hari Ini
                                            </h3>
                                            <p class="font-bold"
                                                :class="isDisplayMode ? (isTVDisplay ? 'text-3xl' : 'text-4xl') : (isTVDisplay ? 'text-xl' : 'text-2xl')">
                                                {{ stats.total_kehadiran_hari_ini || 0 }}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div class="p-6 text-white transition-all duration-300 transform bg-green-500 rounded-lg shadow-lg"
                                    :class="{
                                        'p-8 scale-105 shadow-2xl': isDisplayMode,
                                        'p-4': isTVDisplay
                                    }">
                                    <div class="flex items-center">
                                        <i class="mr-4 fas fa-calendar-day"
                                            :class="isDisplayMode ? (isTVDisplay ? 'text-4xl' : 'text-5xl') : (isTVDisplay ? 'text-2xl' : 'text-3xl')"></i>
                                        <div>
                                            <h3 class="font-semibold"
                                                :class="isDisplayMode ? (isTVDisplay ? 'text-xl' : 'text-2xl') : (isTVDisplay ? 'text-base' : 'text-lg')">
                                                Agenda Hari Ini
                                            </h3>
                                            <p class="font-bold"
                                                :class="isDisplayMode ? (isTVDisplay ? 'text-3xl' : 'text-4xl') : (isTVDisplay ? 'text-xl' : 'text-2xl')">
                                                {{ stats.total_agenda_hari_ini || 0 }}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div class="p-6 text-white transition-all duration-300 transform bg-yellow-500 rounded-lg shadow-lg"
                                    :class="{
                                        'p-8 scale-105 shadow-2xl': isDisplayMode,
                                        'p-4': isTVDisplay
                                    }">
                                    <div class="flex items-center">
                                        <i class="mr-4 fas fa-bullhorn"
                                            :class="isDisplayMode ? (isTVDisplay ? 'text-4xl' : 'text-5xl') : (isTVDisplay ? 'text-2xl' : 'text-3xl')"></i>
                                        <div>
                                            <h3 class="font-semibold"
                                                :class="isDisplayMode ? (isTVDisplay ? 'text-xl' : 'text-2xl') : (isTVDisplay ? 'text-base' : 'text-lg')">
                                                Pengumuman Aktif
                                            </h3>
                                            <p class="font-bold"
                                                :class="isDisplayMode ? (isTVDisplay ? 'text-3xl' : 'text-4xl') : (isTVDisplay ? 'text-xl' : 'text-2xl')">
                                                {{ stats.total_pengumuman_aktif || 0 }}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div class="p-6 text-white transition-all duration-300 transform bg-purple-500 rounded-lg shadow-lg"
                                    :class="{
                                        'p-8 scale-105 shadow-2xl': isDisplayMode,
                                        'p-4': isTVDisplay
                                    }">
                                    <div class="flex items-center">
                                        <i class="mr-4 fas fa-envelope"
                                            :class="isDisplayMode ? (isTVDisplay ? 'text-4xl' : 'text-5xl') : (isTVDisplay ? 'text-2xl' : 'text-3xl')"></i>
                                        <div>
                                            <h3 class="font-semibold"
                                                :class="isDisplayMode ? (isTVDisplay ? 'text-xl' : 'text-2xl') : (isTVDisplay ? 'text-base' : 'text-lg')">
                                                Undangan Hari Ini
                                            </h3>
                                            <p class="font-bold"
                                                :class="isDisplayMode ? (isTVDisplay ? 'text-3xl' : 'text-4xl') : (isTVDisplay ? 'text-xl' : 'text-2xl')">
                                                {{ stats.total_undangan_hari_ini || 0 }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Pengumuman Urgent Section - Hidden in Display Mode -->
                        <div v-if="pengumuman_urgent.length > 0 && !isDisplayMode" class="mb-8">
                            <div class="p-6 bg-white rounded-lg shadow-lg">
                                <h3 class="flex items-center mb-4 text-xl font-bold text-red-600">
                                    <i class="mr-2 fas fa-exclamation-triangle"></i>
                                    Pengumuman Urgent
                                </h3>
                                <div class="space-y-3">
                                    <div v-for="pengumuman in pengumuman_urgent.slice(0, 3)" :key="pengumuman.id"
                                        class="py-2 pl-4 border-l-4 border-red-500">
                                        <h4 class="font-semibold text-gray-800">
                                            {{ pengumuman.judul }}
                                        </h4>
                                        <p class="text-sm text-gray-600">
                                            {{ pengumuman.penerbit }}
                                        </p>
                                        <p class="text-xs text-gray-500">
                                            {{ formatDate(pengumuman.tanggal_publikasi) }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Agenda Section with Animation -->
                        <div v-if="agenda_mendatang.length > 0" :class="isDisplayMode ? 'mb-8 mt-8' : 'mb-8'">
                            <div class="p-6 overflow-hidden transition-all duration-300 bg-white rounded-lg shadow-lg hover:shadow-xl"
                                :class="{
                                    'bg-gradient-to-br from-green-50 to-green-100': isDisplayMode,
                                }" @mouseenter="stopAgendaRotation" @mouseleave="startAgendaRotation">
                                <h3 class="flex items-center mb-6 font-bold text-green-600"
                                    :class="isDisplayMode ? (isTVDisplay ? 'text-2xl' : 'text-3xl') : (isTVDisplay ? 'text-lg' : 'text-xl')">
                                    <i class="mr-3 fas fa-calendar-alt"
                                        :class="isDisplayMode ? (isTVDisplay ? 'text-3xl' : 'text-4xl') : (isTVDisplay ? 'text-lg' : 'text-xl')"></i>
                                    Agenda Mendatang
                                    <span class="ml-auto text-sm font-normal text-gray-500"
                                        :class="isDisplayMode ? (isTVDisplay ? 'text-sm' : 'text-base') : (isTVDisplay ? 'text-xs' : 'text-sm')">
                                        {{ currentAgendaIndex + 1 }} / {{ agenda_mendatang.length }}
                                    </span>
                                </h3>

                                <!-- Animated agenda container -->
                                <div class="relative h-32 overflow-hidden cursor-pointer"
                                    :class="isDisplayMode ? (isTVDisplay ? 'h-36' : 'h-48') : (isTVDisplay ? 'h-24' : 'h-32')"
                                    @click="nextAgenda">
                                    <div class="absolute w-full agenda-container" :style="{
                                        transform: `translateY(-${currentAgendaIndex * (isDisplayMode ? (isTVDisplay ? 144 : 192) : (isTVDisplay ? 96 : 128))
                                            }px)`,
                                    }">
                                        <div v-for="agenda in agenda_mendatang" :key="agenda.id"
                                            class="py-4 pl-6 mb-4 transition-all duration-300 bg-white border-l-4 border-green-500 rounded-r-lg shadow-sm agenda-item hover:shadow-md"
                                            :class="{
                                                'py-6 pl-8 mb-6': isDisplayMode,
                                                'py-3 pl-4 mb-3': isTVDisplay
                                            }" :style="{ height: isDisplayMode ? (isTVDisplay ? '144px' : '192px') : (isTVDisplay ? '96px' : '128px') }">
                                            <h4 class="mb-2 font-semibold text-gray-800"
                                                :class="isDisplayMode ? (isTVDisplay ? 'text-xl mb-2' : 'text-2xl mb-3') : (isTVDisplay ? 'text-base' : 'text-lg')">
                                                {{ agenda.judul }}
                                            </h4>
                                            <div class="space-y-2" :class="{ 'space-y-3': isDisplayMode }">
                                                <p class="flex items-center text-gray-600"
                                                    :class="isDisplayMode ? (isTVDisplay ? 'text-base' : 'text-lg') : (isTVDisplay ? 'text-xs' : 'text-sm')">
                                                    <i class="mr-2 text-green-500 fas fa-map-marker-alt"></i>
                                                    {{ agenda.tempat }}
                                                </p>
                                                <p class="flex items-center text-gray-500"
                                                    :class="isDisplayMode ? (isTVDisplay ? 'text-sm' : 'text-base') : (isTVDisplay ? 'text-xs' : 'text-xs')">
                                                    <i class="mr-2 text-green-500 fas fa-clock"></i>
                                                    {{ formatDate(agenda.tanggal) }} •
                                                    {{ agenda.waktu_mulai }}
                                                </p>
                                                <p v-if="agenda.keterangan" class="flex items-start text-gray-600"
                                                    :class="isDisplayMode ? (isTVDisplay ? 'text-sm' : 'text-base') : (isTVDisplay ? 'text-xs' : 'text-sm')">
                                                    <i class="mt-1 mr-2 text-green-500 fas fa-info-circle"></i>
                                                    {{ agenda.keterangan }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Progress bar -->
                                <div class="w-full h-1 mt-4 overflow-hidden bg-gray-200 rounded-full">
                                    <div class="h-full transition-all duration-300 bg-green-500 rounded-full"
                                        :class="{ 'animate-pulse': !agendaInterval }" :style="{
                                            width: `${((currentAgendaIndex + 1) / agenda_mendatang.length) *
                                                100
                                                }%`,
                                        }"></div>
                                </div>

                                <!-- Navigation dots -->
                                <div class="flex justify-center mt-4 space-x-2">
                                    <button v-for="(agenda, index) in agenda_mendatang" :key="index"
                                        @click="currentAgendaIndex = index" :class="[
                                            'w-3 h-3 rounded-full transition-all duration-200 hover:scale-110',
                                            currentAgendaIndex === index
                                                ? 'bg-green-600 scale-125'
                                                : 'bg-gray-300 hover:bg-gray-400',
                                        ]"></button>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </template>
        <template #footer>
            <!-- Pengumuman Urgent in Display Mode -->
            <div v-if="isDisplayMode && pengumuman_urgent.length > 0"
                class="py-4 text-white border-b border-red-800 shadow-lg bg-gradient-to-r from-red-600 to-red-700 urgent-bg">
                <div class="container px-4 mx-auto">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center mb-2">
                            <i class="mr-3 text-2xl text-yellow-300 fas fa-exclamation-triangle animate-pulse"></i>
                            <h3 class="font-bold" :class="isTVDisplay ? 'text-lg' : 'text-xl'">PENGUMUMAN URGENT</h3>
                        </div>
                        <div class="px-3 py-1 text-sm bg-red-800 rounded-full">
                            {{ pengumuman_urgent.length }} item{{ pengumuman_urgent.length > 1 ? 's' : '' }}
                        </div>
                    </div>

                    <!-- Scrolling urgent announcements -->
                    <div class="relative h-12 overflow-hidden">
                        <div class="absolute w-full transition-transform duration-1000 ease-in-out"
                            :style="{ transform: `translateY(-${currentUrgentIndex * 48}px)` }">
                            <div v-for="pengumuman in pengumuman_urgent" :key="pengumuman.id"
                                class="flex items-center h-12 py-2 urgent-item">
                                <div class="flex-1">
                                    <div class="flex items-center space-x-4">
                                        <span class="font-semibold" :class="isTVDisplay ? 'text-base' : 'text-lg'">{{
                                            pengumuman.judul
                                            }}</span>
                                        <span class="text-red-200">•</span>
                                        <span class="text-red-200">{{ pengumuman.penerbit }}</span>
                                        <span class="text-red-200">•</span>
                                        <span class="text-sm text-red-300">{{ formatDate(pengumuman.tanggal_publikasi)
                                            }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Progress dots for urgent announcements -->
                    <div class="flex justify-center mt-2 space-x-1" v-if="pengumuman_urgent.length > 1">
                        <button v-for="(item, index) in pengumuman_urgent" :key="index"
                            @click="currentUrgentIndex = index" :class="[
                                'w-2 h-2 rounded-full transition-all duration-200',
                                currentUrgentIndex === index ? 'bg-yellow-300' : 'bg-red-400 hover:bg-red-300'
                            ]"></button>
                    </div>
                </div>
            </div>

            <!-- Running Text Pengumuman -->
            <div class="relative py-3 overflow-hidden text-white shadow-lg bg-gradient-to-r from-blue-600 to-blue-700"
                :class="{ 'relative z-50': isDisplayMode }">
                <div class="flex items-center">
                    <div class="flex-shrink-0 px-4 py-2 bg-blue-800 shadow-inner">
                        <i class="mr-2 fas fa-bullhorn animate-pulse"></i>
                        <span class="font-semibold tracking-wide"
                            :class="isTVDisplay ? 'text-xs' : 'text-sm'">PENGUMUMAN</span>
                    </div>
                    <div class="relative flex-1 overflow-hidden bg-blue-600">
                        <div class="flex items-center px-4 py-1 text-sm whitespace-nowrap animate-marquee" :class="{
                            'animate-marquee-fast': isDisplayMode,
                            'text-base': isDisplayMode && !isTVDisplay,
                            'text-sm': isTVDisplay,
                        }" v-if="runningTextContent">
                            {{ runningTextContent }}
                        </div>
                        <div v-else class="px-4 py-1 italic text-blue-200" :class="isTVDisplay ? 'text-xs' : 'text-sm'">
                            <i class="mr-2 fas fa-info-circle"></i>
                            Tidak ada pengumuman terbaru
                        </div>
                    </div>
                    <!-- Right decoration -->
                    <div class="flex-shrink-0 px-2 py-2 bg-blue-800">
                        <i class="text-blue-300 fas fa-chevron-right"></i>
                    </div>
                </div>

                <!-- Optional: Current time display in footer -->
                <div v-if="isDisplayMode" class="absolute transform -translate-y-1/2 right-4 top-1/2">
                    <span class="px-2 py-1 text-xs bg-blue-800 rounded">
                        {{ currentTime }}
                    </span>
                </div>
            </div>

        </template>
    </Container>
</template>

<script setup>
import {
    defineProps,
    provide,
    computed,
    ref,
    onMounted,
    onUnmounted,
} from "vue";
import { router } from '@inertiajs/vue3';
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import FooterFe from "@/Components/FooterFe.vue";

const props = defineProps({

    // FEB Info specific props
    kehadiran_hari_ini: {
        type: Array,
        default: () => [],
    },
    agenda_hari_ini: {
        type: Array,
        default: () => [],
    },
    agenda_mendatang: {
        type: Array,
        default: () => [],
    },
    pengumuman_aktif: {
        type: Array,
        default: () => [],
    },
    pengumuman_urgent: {
        type: Array,
        default: () => [],
    },
    undangan_hari_ini: {
        type: Array,
        default: () => [],
    },
    undangan_mendatang: {
        type: Array,
        default: () => [],
    },
    stats: {
        type: Object,
        default: () => ({}),
    },
});



// Provide FEB Info data
provide("kehadiran_hari_ini", props.kehadiran_hari_ini);
provide("agenda_hari_ini", props.agenda_hari_ini);
provide("agenda_mendatang", props.agenda_mendatang);
provide("pengumuman_aktif", props.pengumuman_aktif);
provide("pengumuman_urgent", props.pengumuman_urgent);
provide("undangan_hari_ini", props.undangan_hari_ini);
provide("undangan_mendatang", props.undangan_mendatang);
provide("stats", props.stats);


// Clock computeds
const currentTime = computed(() => {
    return currentTimeRef.value.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });
});

const currentDate = computed(() => {
    return currentTimeRef.value.toLocaleDateString("id-ID", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    });
});

// TV Display detection and optimization
const isTVDisplay = computed(() => {
    return screenWidth.value === 1280 && screenHeight.value === 720;
});

const isLargeTVDisplay = computed(() => {
    return screenWidth.value >= 1280 && screenHeight.value >= 720;
});

const displayOptimization = computed(() => {
    if (!isDisplayMode.value) return 'normal';
    if (isTVDisplay.value) return 'tv-720p';
    if (isLargeTVDisplay.value) return 'tv-large';
    return 'desktop';
});

// Auto-refresh functionality
const autoRefreshEnabled = ref(false);
const autoRefreshInterval = ref(30); // seconds
const autoRefreshTimer = ref(null);
const lastRefreshTime = ref(null);
const refreshCountdown = ref(0);
const countdownTimer = ref(null);
const showRefreshSuccess = ref(false);
const showAutoRefreshPanel = ref(false);

// Auto-refresh methods
const startAutoRefresh = () => {
    if (autoRefreshTimer.value) {
        clearInterval(autoRefreshTimer.value);
    }
    if (countdownTimer.value) {
        clearInterval(countdownTimer.value);
    }

    autoRefreshEnabled.value = true;
    refreshCountdown.value = autoRefreshInterval.value;

    // Start countdown
    countdownTimer.value = setInterval(() => {
        refreshCountdown.value--;
        if (refreshCountdown.value <= 0) {
            refreshPage();
            refreshCountdown.value = autoRefreshInterval.value;
        }
    }, 1000);

    // Set main refresh timer
    autoRefreshTimer.value = setInterval(() => {
        refreshPage();
    }, autoRefreshInterval.value * 1000);

    lastRefreshTime.value = new Date();
};

const stopAutoRefresh = () => {
    autoRefreshEnabled.value = false;
    if (autoRefreshTimer.value) {
        clearInterval(autoRefreshTimer.value);
        autoRefreshTimer.value = null;
    }
    if (countdownTimer.value) {
        clearInterval(countdownTimer.value);
        countdownTimer.value = null;
    }
    refreshCountdown.value = 0;
};

const refreshPage = () => {
    lastRefreshTime.value = new Date();
    // Use Inertia.js reload to refresh data without full page reload
    router.reload({
        only: ['kehadiran_hari_ini', 'agenda_mendatang', 'pengumuman_aktif', 'pengumuman_urgent', 'stats'],
        preserveScroll: true,
        onSuccess: () => {
            // Show brief success indicator
            showRefreshSuccess.value = true;
            setTimeout(() => {
                showRefreshSuccess.value = false;
            }, 2000);
        }
    });
};

const setRefreshInterval = (seconds) => {
    autoRefreshInterval.value = seconds;
    if (autoRefreshEnabled.value) {
        stopAutoRefresh();
        startAutoRefresh();
    }
};

const formatLastRefreshTime = computed(() => {
    if (!lastRefreshTime.value) return '';
    return lastRefreshTime.value.toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
});// Running text content computed
const runningTextContent = computed(() => {
    const urgentPengumuman = (props.pengumuman_urgent || []).map((p) => ({
        ...p,
        priority: "urgent",
    }));
    const aktivePengumuman = (props.pengumuman_aktif || []).map((p) => ({
        ...p,
        priority: "active",
    }));

    const allPengumuman = [...urgentPengumuman, ...aktivePengumuman];

    if (allPengumuman.length === 0) return null;

    const textItems = allPengumuman.map((p) => {
        const icon = p.priority === "urgent" ? "🚨" : "📢";
        const date = p.tanggal_publikasi ? formatDate(p.tanggal_publikasi) : "";
        return `${icon} ${p.judul} - ${p.penerbit} ${date ? `(${date})` : ""}`;
    });

    // Add some spacing and repeat for continuous scroll
    return textItems.join(" • ") + " • " + textItems.join(" • ") + " • ";
});

// Carousel state
const currentSlide = ref(0);
const carouselInterval = ref(null);

// Display mode state
const isDisplayMode = ref(false);
const currentTimeRef = ref(new Date());

// TV Display detection
const screenWidth = ref(window.innerWidth);
const screenHeight = ref(window.innerHeight);

// Agenda animation state
const currentAgendaIndex = ref(0);
const agendaInterval = ref(null);

// Urgent announcement animation state
const currentUrgentIndex = ref(0);
const urgentInterval = ref(null);

// Image error handling
const imageErrors = ref({});
const imageLoaded = ref({});

// Helper function to format dates
const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

// Helper function to get status label
const getStatusLabel = (status) => {
    const labels = {
        hadir: "Hadir",
        tidak_hadir: "Tidak Hadir",
        terlambat: "Terlambat",
        sakit: "Sakit",
        izin: "Izin",
    };
    return labels[status] || status;
};

// Helper function to get status CSS class
const getStatusClass = (status) => {
    const classes = {
        hadir: "bg-green-100 text-green-800",
        tidak_hadir: "bg-red-100 text-red-800",
        terlambat: "bg-yellow-100 text-yellow-800",
        sakit: "bg-blue-100 text-blue-800",
        izin: "bg-purple-100 text-purple-800",
    };
    return classes[status] || "bg-gray-100 text-gray-800";
};

// Split kehadiran data into slides (6 items per slide)
const kehadiranSlides = computed(() => {
    const itemsPerSlide = 6;
    const slides = [];
    const data = props.kehadiran_hari_ini || [];

    for (let i = 0; i < data.length; i += itemsPerSlide) {
        slides.push(data.slice(i, i + itemsPerSlide));
    }

    return slides;
});

// Carousel navigation functions
const nextSlide = () => {
    if (currentSlide.value < kehadiranSlides.value.length - 1) {
        currentSlide.value++;
    } else {
        currentSlide.value = 0; // Loop back to first slide
    }
};

const prevSlide = () => {
    if (currentSlide.value > 0) {
        currentSlide.value--;
    } else {
        currentSlide.value = kehadiranSlides.value.length - 1; // Loop to last slide
    }
};

// Agenda rotation functions
const nextAgenda = () => {
    if (props.agenda_mendatang && props.agenda_mendatang.length > 0) {
        if (currentAgendaIndex.value < props.agenda_mendatang.length - 1) {
            currentAgendaIndex.value++;
        } else {
            currentAgendaIndex.value = 0; // Loop back to first agenda
        }
    }
};

const startAgendaRotation = () => {
    if (props.agenda_mendatang && props.agenda_mendatang.length > 1) {
        agendaInterval.value = setInterval(
            () => {
                nextAgenda();
            },
            isDisplayMode.value ? 4000 : 6000
        ); // 4s in display mode, 6s in normal mode
    }
};

const stopAgendaRotation = () => {
    if (agendaInterval.value) {
        clearInterval(agendaInterval.value);
        agendaInterval.value = null;
    }
};

// Urgent announcement rotation functions
const nextUrgent = () => {
    if (props.pengumuman_urgent && props.pengumuman_urgent.length > 0) {
        if (currentUrgentIndex.value < props.pengumuman_urgent.length - 1) {
            currentUrgentIndex.value++;
        } else {
            currentUrgentIndex.value = 0; // Loop back to first urgent
        }
    }
};

const startUrgentRotation = () => {
    if (props.pengumuman_urgent && props.pengumuman_urgent.length > 1) {
        urgentInterval.value = setInterval(() => {
            nextUrgent();
        }, 3000); // 3 seconds for urgent announcements
    }
};

const stopUrgentRotation = () => {
    if (urgentInterval.value) {
        clearInterval(urgentInterval.value);
        urgentInterval.value = null;
    }
};

// Clock update function
const updateClock = () => {
    currentTimeRef.value = new Date();
};

// Screen resize handler
const updateScreenSize = () => {
    screenWidth.value = window.innerWidth;
    screenHeight.value = window.innerHeight;
};

// Image handling functions
const handleImageError = (id) => {
    imageErrors.value[id] = true;
};

const handleImageLoad = (id) => {
    imageLoaded.value[id] = true;
    imageErrors.value[id] = false;
};

const getPhotoUrl = (foto) => {
    if (!foto) return '';
    // Handle both full URLs and relative paths
    if (foto.startsWith('http')) return foto;
    return `/storage/${foto}`;
};

// Display mode functions
const toggleDisplayMode = () => {
    isDisplayMode.value = !isDisplayMode.value;
    if (isDisplayMode.value) {
        document.body.style.overflow = "hidden";
        // Auto-refresh carousel every 5 seconds in display mode
        carouselInterval.value = setInterval(() => {
            nextSlide();
        }, 5000);
        // Update clock every second
        setInterval(updateClock, 1000);
        // Start agenda rotation
        startAgendaRotation();
        // Start urgent announcements rotation in display mode
        startUrgentRotation();

        // Auto-start refresh if it was enabled
        if (autoRefreshEnabled.value) {
            startAutoRefresh();
        }

        // Add escape key listener
        document.addEventListener("keydown", handleEscapeKey);
    } else {
        document.body.style.overflow = "auto";
        // Clear carousel auto-refresh when exiting display mode
        if (carouselInterval.value) {
            clearInterval(carouselInterval.value);
            carouselInterval.value = null;
        }
        // Stop agenda rotation
        stopAgendaRotation();
        // Stop urgent announcements rotation
        stopUrgentRotation();

        // Stop auto-refresh when exiting display mode (optional)
        // stopAutoRefresh();

        // Remove escape key listener
        document.removeEventListener("keydown", handleEscapeKey);
    }
}; // Handle escape key to exit display mode
const handleEscapeKey = (event) => {
    if (event.key === "Escape" && isDisplayMode.value) {
        toggleDisplayMode();
    }
};

// Start agenda rotation on component mount

onMounted(() => {
    startAgendaRotation();
    window.addEventListener('resize', updateScreenSize);
    updateScreenSize(); // Initial size detection

    // Auto-start refresh in display mode if enabled
    if (isDisplayMode.value && autoRefreshEnabled.value) {
        startAutoRefresh();
    }

    // Close panel when clicking outside
    document.addEventListener('click', handleClickOutside);
});

const handleClickOutside = (event) => {
    const panel = document.querySelector('.auto-refresh-panel');
    const button = document.querySelector('.floating-refresh-btn');

    if (showAutoRefreshPanel.value && panel && button) {
        if (!panel.contains(event.target) && !button.contains(event.target)) {
            showAutoRefreshPanel.value = false;
        }
    }
};

onUnmounted(() => {
    stopAgendaRotation();
    stopUrgentRotation();
    stopAutoRefresh(); // Clean up auto-refresh timers
    window.removeEventListener('resize', updateScreenSize);
    document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
@keyframes marquee {
    0% {
        transform: translateX(100%);
    }

    100% {
        transform: translateX(-100%);
    }
}

.animate-marquee {
    animation: marquee 45s linear infinite;
    transition: all 0.3s ease;
}

.animate-marquee-fast {
    animation: marquee 25s linear infinite;
    transition: all 0.3s ease;
}

/* Pause animation on hover */
.animate-marquee:hover,
.animate-marquee-fast:hover {
    animation-play-state: paused;
    color: #fef3c7;
    /* Light yellow on hover */
}

/* Responsive text size */
@media (max-width: 768px) {

    .animate-marquee,
    .animate-marquee-fast {
        font-size: 0.75rem;
    }
}

/* Smooth gradient background */
.bg-gradient-to-r {
    background-size: 200% 200%;
    animation: gradientShift 8s ease infinite;
}

@keyframes gradientShift {
    0% {
        background-position: 0% 50%;
    }

    50% {
        background-position: 100% 50%;
    }

    100% {
        background-position: 0% 50%;
    }
}

/* Pulse effect for urgent announcements */
@keyframes urgentPulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.8;
    }
}

.urgent-text {
    animation: urgentPulse 2s ease-in-out infinite;
}

/* Agenda slide animation */
@keyframes slideUpFade {
    0% {
        opacity: 0.8;
        transform: translateY(10px);
    }

    100% {
        opacity: 1;
        transform: translateY(0);
    }
}

.agenda-item {
    animation: slideUpFade 0.5s ease-out forwards;
}

/* Smooth transition for agenda container */
.agenda-container {
    transition: transform 1s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Progress bar for agenda rotation */
@keyframes progressBar {
    0% {
        width: 0%;
    }

    100% {
        width: 100%;
    }
}

/* Urgent announcement animations */
@keyframes urgentSlideUp {
    0% {
        opacity: 0.7;
        transform: translateY(5px);
    }

    100% {
        opacity: 1;
        transform: translateY(0);
    }
}

.urgent-item {
    animation: urgentSlideUp 0.5s ease-out forwards;
}

/* Urgent footer gradient animation */
@keyframes urgentGradient {
    0% {
        background-position: 0% 50%;
    }

    50% {
        background-position: 100% 50%;
    }

    100% {
        background-position: 0% 50%;
    }
}

.urgent-bg {
    background-size: 200% 200%;
    animation: urgentGradient 4s ease infinite;
}

/* Photo hover effects */
.kehadiran-photo {
    transition: all 0.3s ease;
}

.kehadiran-photo:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Status badge animations */
@keyframes statusPulse {

    0%,
    100% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.02);
    }
}

.status-badge {
    animation: statusPulse 3s ease-in-out infinite;
    transition: all 0.3s ease;
}

.status-badge:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Online status indicator pulse */
@keyframes statusIndicatorPulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.7;
    }
}

.status-indicator {
    animation: statusIndicatorPulse 2s ease-in-out infinite;
}

/* Auto-refresh animations */
@keyframes refreshSuccess {
    0% {
        opacity: 0;
        transform: translateY(-10px);
    }

    50% {
        opacity: 1;
        transform: translateY(0);
    }

    100% {
        opacity: 0;
        transform: translateY(-5px);
    }
}

.refresh-success {
    animation: refreshSuccess 2s ease-in-out forwards;
}

@keyframes countdownPulse {

    0%,
    100% {
        transform: scale(1);
        opacity: 1;
    }

    50% {
        transform: scale(1.05);
        opacity: 0.8;
    }
}

.countdown-urgent {
    animation: countdownPulse 1s ease-in-out infinite;
    color: #ef4444;
    font-weight: 600;
}

/* Auto-refresh panel animations */
@keyframes slideInRight {
    0% {
        transform: translateX(100%);
        opacity: 0;
    }

    100% {
        transform: translateX(0);
        opacity: 1;
    }
}

@keyframes slideOutRight {
    0% {
        transform: translateX(0);
        opacity: 1;
    }

    100% {
        transform: translateX(100%);
        opacity: 0;
    }
}

.panel-enter-active {
    animation: slideInRight 0.3s ease-out forwards;
}

.panel-leave-active {
    animation: slideOutRight 0.3s ease-in forwards;
}

/* Floating button hover effect */
.floating-refresh-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.floating-refresh-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
}

/* Display mode layout improvements */
.display-mode-container {
    /* Ensure proper spacing in display mode */
    min-height: 100vh;
    padding-top: 2rem;
    padding-bottom: 2rem;
}

.display-mode-container .grid>div {
    /* Add consistent spacing between grid items */
    margin-bottom: 1.5rem;
}

/* Improve scrollbar styling for display mode */
.display-mode-container::-webkit-scrollbar {
    width: 8px;
}

.display-mode-container::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
}

.display-mode-container::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
}

.display-mode-container::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
}
</style>
